import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SharedModule } from '@shared/shared.module';
import { DocumentAnalysisSituation } from '@interfaces/accounting-data-standardization/responses/document-analysis-situation.response';
import {
  analysisSituationMessageParts,
  analysisSituationTag,
} from '@src/app/features/dashboard/accounting-data-standardization/utils/analysis-situation.util';

/**
 * Célula "Situação na análise" da lista da Padronização (card 3700, opção A do protótipo): uma tag e a frase pronta do
 * backend dizendo se o documento vale, por que não vale e o que fazer. O nome do documento que vale no lugar deste vira
 * link (`coveredOpened`), e o único documento de um ano sem validado ganha o botão "Conferir e validar" (`reviewRequested`).
 *
 * Não calcula nada: a situação, o período e a frase vêm do backend. Sem situação (nula ou backend anterior), não
 * renderiza nada.
 */
@Component({
  selector: 'app-document-analysis-situation',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './document-analysis-situation.component.html',
  styleUrl: './document-analysis-situation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentAnalysisSituationComponent {
  readonly situation = input<DocumentAnalysisSituation | null | undefined>(null);

  /** Abrir o documento que vale no lugar deste (id do outro documento). */
  readonly coveredOpened = output<string>();
  /** "Conferir e validar": abrir este documento. */
  readonly reviewRequested = output<void>();

  protected readonly tag = computed(() => analysisSituationTag(this.situation()));
  protected readonly messageParts = computed(() => analysisSituationMessageParts(this.situation()));
  protected readonly isEffective = computed(() => this.situation()?.kind === 'Effective');
  protected readonly isOnlyDocumentForYear = computed(
    () => this.situation()?.kind === 'OnlyDocumentForYear'
  );
  /** Em `Effective` a frase ("Vale para jan–set 2026") já é a própria marca verde; sem ela, o rótulo da tag. */
  protected readonly effectiveText = computed(
    () => this.situation()?.message?.trim() || this.tag()?.label || ''
  );

  protected onCoveredClick(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    const id = this.situation()?.coveredByExtractionId;
    if (id) this.coveredOpened.emit(id);
  }

  protected onReview(): void {
    this.reviewRequested.emit();
  }
}
